'use client';
import { AxiosError } from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import PaginationButtons from 'src/components/shared/components/pagination-buttons';

import { APIErrorResponseGenerator } from '@/lib/helper';

import Container from '@/components/@base/container';
import { showToast } from '@/components/@base/toast/show-toast';
import { useClientFilterParams } from '@/components/app/archive/helpers/hooks';
import Auth from '@/components/app/auth/components';
import CommentCard from '@/components/app/product/components/@shared/comment-card';
import Loading from '@/components/app/product/components/@shared/comment-card/loading';
import Header from '@/components/app/product/components/@shared/comments-header';
import Section from '@/components/app/product/components/@shared/section';
import { AiComment } from '@/components/app/product/components/comments/ai-comment';
import Props from '@/components/app/product/components/comments/type';
import {
  useGetAddOnsInfo,
  useGetAiReviewSummary,
  useGetCommentList,
  usePostVote,
} from '@/components/app/product/services/hooks';
import { useGetProfile } from '@/components/layout/services/hooks';
import { loadingGenerator } from '@/components/shared/helpers';
import useScrollTo from '@/components/shared/helpers/hooks/use-scroll-to';

import useCommonModalStore from '@/store/common-modal-store';

import { DATA_CY_PRODUCT_COMMENTS } from '@/constant/cypress';
import { pageLevelLocalization } from '@/constant/localization';
import CommentModel from '@/models/comment.model';
const { product: productLocalization } = pageLevelLocalization;

export default function Comments({
  slug,
  rate,
  productId,
  handleAddCommentClick,
  aiReviewSummary,
}: Props) {
  const { filterParams, updateParams } = useClientFilterParams(); //TODO: change to useFilterParams
  const scrollToNode = useScrollTo();
  const headerRef = useRef<HTMLDivElement>(null);
  const replyParentRefs = useRef(new Map());
  const [commentList, setCommentList] = useState<CommentModel[]>([]);
  const [isVotePending, setIsVotePending] = useState();
  const { data: addonsData } = useGetAddOnsInfo({ slug });

  const { profile, isAuthenticated } = useGetProfile();
  // Authenticated re-fetch of the AI summary — only this one carries `currentUserVote`.
  const { data: userAiReviewSummary, refetch: refetchAiReviewSummary } = useGetAiReviewSummary({
    slug,
    enabled: isAuthenticated,
  });
  const aiSummary = userAiReviewSummary ?? aiReviewSummary;
  const { data, refetch, isPending } = useGetCommentList({
    slug: productId,
    filters: filterParams,
    page: filterParams['page'] ?? 1,
  });
  // let isPending = true;
  const { setShow } = useCommonModalStore();
  const { mutate: postVote } = usePostVote();

  const openLoginModal = () =>
    setShow(true, {
      DialogPanelProps: {
        className: 'rounded-xl overflow-hidden shadow-none!  p-0! bg-transparent! ',
      },
      Body() {
        return <Auth />;
      },
    });
  useEffect(() => {
    if (!data) {
      return;
    }
    setCommentList(data.comments);
  }, [data]);

  useEffect(() => {
    refetch();
  }, [filterParams['page'], filterParams['badge']]);

  const handlePageChange = (page: number) => {
    updateParams('page', page, false, { scroll: false });
    scrollToNode(headerRef.current, 80);
  };

  const handleVote = (id, vote, isAi = false) => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    setIsVotePending(id);
    postVote(
      { id, vote },
      {
        onSuccess: async () => {
          refetch();
          setIsVotePending(undefined);
          if (isAi) {
            refetchAiReviewSummary();
            showToast({ message: productLocalization.registered, type: 'success' });
          }
        },
        onError: (error) => {
          if (error instanceof AxiosError) {
            showToast(APIErrorResponseGenerator(error));
          }
          setIsVotePending(undefined);
        },
      },
    );
  };
  return (
    <>
      <Section
        className='mx-1! w-full! max-w-sm! min-w-sm flex-1 p-0! md:max-w-full! md:min-w-full!'
        data-cy={DATA_CY_PRODUCT_COMMENTS}
      >
        <Header
          rate={rate}
          className='m-0! w-full px-3.5 py-[15px] md:px-[30px] md:pt-8 md:pb-5'
          onAddCommentClick={handleAddCommentClick}
          ref={headerRef}
          data={addonsData}
          isAuthenticated={isAuthenticated}
        />
        <Container className='border-t-[1px] border-t-[#F4F5F6] px-3.5 py-[15px]! pt-[30px]! md:px-[30px]'>
          {aiSummary && (
            <AiComment
              aiReviewSummary={aiSummary}
              handleVote={handleVote}
              isVotePending={isVotePending}
            />
          )}
          {isPending ? (
            loadingGenerator(12, () => <Loading />)
          ) : (
            <>
              {commentList.length > 0 &&
                commentList.map((comment) => (
                  <Container
                    key={comment.id}
                    className='mb-5 overflow-hidden rounded-lg border-[1px] border-[#E9EBF1] p-[15px] last:mb-0'
                  >
                    <CommentCard
                      {...comment}
                      handleVote={handleVote}
                      isVotePending={isVotePending}
                      rate={rate}
                      slug={slug}
                      productId={productId}
                      ref={(node) => {
                        node
                          ? replyParentRefs.current.set(comment.id, node)
                          : replyParentRefs.current.delete(comment.id);
                      }}
                    />
                  </Container>
                ))}

              {data && data?.totalPages !== null && data.totalPages > 1 && (
                <PaginationButtons
                  totalPages={data?.totalPages ?? undefined}
                  currentPage={data.currentPage ?? undefined}
                  onPageChange={handlePageChange}
                  className='my-4'
                />
              )}
            </>
          )}
        </Container>
      </Section>
    </>
  );
}
